import { APIRoutes } from "../../constants";
import { ProjectApiDataResponse } from "../../pages/Projects/types";
import { RequestServer } from "../../utils/services";
import { ProjectData, InvitationDetail, TpeAgendaPatchPayload, TSubmitAgendaResponse, TCategorizedAgenda, TGetPEAgenda, TAgendaResponse, TAgendaItem, TAgendaStatus } from "./type";
import { generateUniqueId } from "../../utils/utils";
import { ChipProps } from '@mui/material/Chip';
import { isExpert } from "../../utils/role";

export const PeAgendaStatus = {
  SENT: "Sent",
  RESPONDED_BY_ADMIN: "RespondedByAdmin",
  RESPONDED_BY_EXPERT: "RespondedByExpert",
  DECLINED: "Declined",
} as const;

export const defaultValuesOfProjectData = {
  topic: "",
  description: "",
  account_manager_value: {
    id: -1,
    name: '',
    email: '',
    mobile: '',
  }
}

export const steps = {
  mobile: {
    invitation: "Project", 
    agenda: "Agenda",
    confirmation: "Terms",
  }, desktop: {
    invitation: "Project Invitation",
    agenda: "Agenda",
    confirmation: "Confirmation",
  }
} as const

export const stepsWithAgenda = ["Project Invitation", "Confirmation", "Agenda"];
export const stepsWithoutAgenda = ["Project Invitation", "Confirmation"];
export const stepsWithAgendaMobile = ["Project", "Terms", "Agenda"];
export const stepsWithoutAgendaMobile = ["Project", "Terms"];

export function getStepsDesktop(isAgenda: boolean, showAcceptInvitationForm: boolean): string[] {
  const desktopSteps: string[] = [];

  if (showAcceptInvitationForm) {
    desktopSteps.push(steps.desktop.invitation);
  }

  const addConfirmation = () => desktopSteps.push(steps.desktop.confirmation);
  const addAgenda = () => desktopSteps.push(steps.desktop.agenda);

  if (isAgenda) {
    if (showAcceptInvitationForm) {
      addConfirmation();
      addAgenda();
    } else {
      addAgenda();
    }
  } else {
    addConfirmation();
  }

  return desktopSteps;
}

export function getStepsMobile(isAgenda: boolean, showAcceptInvitationForm:boolean){
  let mobileSteps: string[] = []

  if (showAcceptInvitationForm){
    mobileSteps.push(steps.mobile.invitation)
  } 
  const addConfirmation = () => mobileSteps.push(steps.desktop.confirmation);
  const addAgenda = () => mobileSteps.push(steps.desktop.agenda);
  if (isAgenda) {
    if (showAcceptInvitationForm) {
      addConfirmation();
      addAgenda();
    } else {
      addAgenda();
    }
  } else {
    addConfirmation();
  }
  return mobileSteps

}

// Format of ID: data---shared_link_id---project_id---pe_id---expert_id---user_id---client_type
// if Agenda is shared, then ---agendaid is also there

export function getInvitationData(id: string) {
  const decipheredData = atob(id);
  const data = decipheredData.split("---");
  const output: InvitationDetail = {
    date: data[0],
    sharedLinkId: data[1],
    projectId: data[2],
    peId: data[3],
    expertId: data[4],
    userId: data[5],
    clientType: data[6] ,
    agendaId: data[7],
  };

  return output;
}

export async function getProjectData(
  projectId: string,
  expertId: string
): Promise<ProjectData | null> {
  try {
    const apiQuery = APIRoutes.peMapping + "?fk_project=" + projectId + "&fk_expert=" + expertId + '&embed=YES&notnull___expert_invitation=1' + '&show_columns=fk_project_value,id';

    const response = await RequestServer(
      apiQuery,
      "GET"
    );

    if (response && response.data) {
      const data = response.data[0];
      return {
        topic: data?.fk_project_value.external_topic,
        description: data?.fk_project_value.description,
        account_manager_value: data?.fk_project_value.account_manager_value,
      };
    } else {
      return null;
    }
  } catch (e) {
    console.error(e);
    return null;
  }
}
export async function rejectInvitation(invitationId: string) {
  const payload = {
    action: "RejectInvitation",
    id: invitationId,
  };

  const response = await RequestServer(APIRoutes.peMapping, "PATCH", payload);
  return response;
}
export async function acceptInvitation(invitationId: string) {
  const payload = {
    action: "AcceptInvitation",
    id: invitationId,
  };

  const response = await RequestServer(APIRoutes.peMapping, "PATCH", payload);
  return response;
}

export async function acceptAgreement(expertId: string) {

  const payload = {
    action: "AcceptAgreement",
    id: expertId,
  };

  const response = await RequestServer(APIRoutes.expertAgreement, "PATCH", payload);
  return response;
}

export async function declineAgenda({ expertId, peAgendaId, projectId, peId }: TpeAgendaPatchPayload) {

  const payload = {
    action: "Declined",
    expert_id: expertId,
    pe_id: peId,
    project_id: projectId,
    pe_agenda_id: [peAgendaId]
  };

  const response = await RequestServer(APIRoutes.PE_AGENDA, "PATCH", payload);
  return response;
}

export async function revokeAgenda({ expertId, peAgendaId, projectId, peId }: TpeAgendaPatchPayload) {

  const payload = {
    action: "Revoked",
    expert_id: expertId,
    pe_id: peId,
    project_id: projectId,
    pe_agenda_id: [peAgendaId]
  };

  const response = await RequestServer(APIRoutes.PE_AGENDA, "PATCH", payload);
  return response;
}

export async function submitAgendaResponse({ pe_agenda_id, pe_id, project_id, agendaResponse, agenda_id }: TSubmitAgendaResponse) {

  const payload = {
    action: "SubmitAgendaResponse",
    pe_id,
    project_id,
    agenda_id,
    pe_agenda_id,
    agenda_responses: agendaResponse,
  };

  const response = await RequestServer(APIRoutes.PE_AGENDA, "PATCH", payload);
  return response;
}

export const getPeAgenda = async ({ fk_project, fk_pe, fk_agenda, setPeAgendaData }: TGetPEAgenda) => {
  try {
    let url: string;
    // for compatibility with old links we will use agenda id
    if (fk_agenda) {
      url = `${APIRoutes.PE_AGENDA}?fk_project=${fk_project}&fk_pe=${fk_pe}&fk_agenda=${fk_agenda}&in___status=Sent,RespondedByExpert,RespondedByAdmin,Declined&embed=YES&show_columns=agenda_responses,fk_agenda,id,status`;
    } else {
      url = `${APIRoutes.PE_AGENDA}?fk_project=${fk_project}&fk_pe=${fk_pe}&in___status=Sent,RespondedByExpert,RespondedByAdmin,Declined&embed=YES&show_columns=agenda_responses,fk_agenda,id,status`;
    }
    const response = await RequestServer(url, "get");
    if (response.success) {
      setPeAgendaData(categorizeAgendas(response.data));
    }
  } catch (error) {
    console.log(error);
  }
}

function formatAgendaResponsesWithFlags(item: TAgendaItem) {
  const questions: string[] = item.questions || [];
  const meta: any = item?.meta || [];
  const agendaResponses: TAgendaResponse[] = item.agenda_responses || [];

  const updatedResponses = questions.map((questionText, index) => {
    const matchedResponse = agendaResponses.find(
      (resp) => resp?.question?.trim() === questionText?.trim()
    );

    if (matchedResponse) {
      return {
        ...matchedResponse,
        inputId: generateUniqueId(),
        expert_note_required: meta[index]?.expert_note_required || false,
        isNewQuestion: false,
      };
    }

    return {
      inputId: generateUniqueId(),
      question: questionText,
      answer: "",
      expert_note: "",
      expert_note_required: meta[index]?.expert_note_required || false,
      isNewQuestion: true,
    };
  });

  return {
    ...item,
    formattedAgenda: updatedResponses,
  };
}

function addInputIdsToResponses(item: TAgendaItem) {
  const responses = item.agenda_responses;
  const updatedResponses = responses.map(resp => ({
    ...resp,
    inputId: generateUniqueId(),
  }));

  return {
    ...item,
    formattedAgenda: updatedResponses,
  };
}

function categorizeAgendas(data: any[]): TCategorizedAgenda {
  const result: TCategorizedAgenda = {
    sentAgenda: [],
    respondedByAdminAgenda: [],
    respondedByExpertAgenda: [],
    declineAgenda: [],
  };

  data.forEach(rawItem => {
    const baseItem: TAgendaItem = {
      id: rawItem.id,
      fk_agenda: rawItem.fk_agenda,
      status: rawItem.status === 'Sent'
        ? rawItem.agenda_responses?.length
          ? 'Reshared'
          : 'NewAgenda'
        : rawItem.status,
      agenda_responses: rawItem.agenda_responses || [],
      title: rawItem.fk_agenda_value?.title || null,
      questions: rawItem.fk_agenda_value?.agenda?.questions || [],
      meta: rawItem.fk_agenda_value?.agenda?.meta || [],
    };

    switch (rawItem.status) {
      case PeAgendaStatus.SENT: {
        const formatted = formatAgendaResponsesWithFlags(baseItem);
        result.sentAgenda.push(formatted);
        break;
      }

      case PeAgendaStatus.RESPONDED_BY_ADMIN: {
        const withInputIds = addInputIdsToResponses(baseItem);
        result.respondedByAdminAgenda.push(withInputIds);
        break;
      }

      case PeAgendaStatus.RESPONDED_BY_EXPERT: {
        const withInputIds = addInputIdsToResponses(baseItem);
        result.respondedByExpertAgenda.push(withInputIds);
        break;
      }

      case PeAgendaStatus.DECLINED: {
        const formatted = formatAgendaResponsesWithFlags(baseItem);
        result.declineAgenda.push(formatted);
        break;
      }
      default:
        console.warn(`Unknown status: ${rawItem.status}`);
    }
  });

  return result;
}


// Helper functions for status handling
export const getAgendaStatusText = (status: TAgendaStatus | 'Reshared' | 'NewAgenda') => {
  const isRoleExpert = isExpert()
  const statusTexts: Record<string, string> = {
    'NewAgenda': 'New Agenda',
    'Declined': 'Declined',
    'RespondedByAdmin': 'Responded by POC',
    'RespondedByExpert':  isRoleExpert ? 'Responded by You' : 'Responded by Expert',
    'Reshared': 'Reshared',
    'Sent': 'Shared'
  };

  return statusTexts[status];
};

export const getAgendaStatusColor = (status: TAgendaStatus | 'Reshared' | 'NewAgenda'): ChipProps['color'] => {
  const statusColors: Record<string, ChipProps['color']> = {
    'NewAgenda': 'info',
    'Declined': 'error',
    'RespondedByAdmin': 'success',
    'RespondedByExpert': 'success',
    'Reshared': 'warning',
  };

  return statusColors[status] || 'default';
};